import { ExternalLink, RefreshCw, Store } from 'lucide-react'
import type { QuickShipListing } from '@/api/warehouse'
import { LoadingButton } from '@/components/loading-button'
import { POInlineEditableField } from '@/components/po-inline-edit'
import { StatusPill } from '@/components/status-pill'
import { Card, CardContent } from '@/components/ui/card'
import type { StatusTone } from '@/lib/status-tones'
import { formatMoney } from '@/lib/utils'

const LISTING_STATUS: Record<QuickShipListing['status'], { tone: StatusTone; label: string }> = {
  draft: { tone: 'neutral', label: 'Not on the store yet' },
  active: { tone: 'success', label: 'On the store' },
  sold_out: { tone: 'warning', label: 'Sold out' },
  archived: { tone: 'neutral', label: 'Archived' },
}

function ExternalLinkText({ href, children }: { href: string; children: string }) {
  return (
    // eslint-disable-next-line no-restricted-syntax -- external URL
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
    >
      {children} <ExternalLink className="size-3" aria-hidden />
    </a>
  )
}

/**
 * The store listing this lot sells through (one per product + condition): its title, price
 * (click to change; cleared goes back to the suggested price), how many it offers, and links.
 */
export function QuickShipListingBlock({
  listing,
  onSavePrice,
  pricePending = false,
  onPushNow,
  pushPending = false,
}: {
  listing: QuickShipListing | null | undefined
  onSavePrice?: (price: string | null) => void
  pricePending?: boolean
  onPushNow?: () => void
  pushPending?: boolean
}) {
  const status = listing ? LISTING_STATUS[listing.status] : null
  const effectivePrice = listing?.price ?? listing?.suggested_price ?? null

  return (
    <Card className="gap-0 py-0">
      <CardContent className="space-y-3 p-4">
        <div className="flex items-center justify-between gap-2">
          <h2 className="flex items-center gap-2 text-sm font-semibold">
            <Store className="size-4 text-muted-foreground" aria-hidden />
            Quick Ship listing
          </h2>
          {status && <StatusPill tone={status.tone}>{status.label}</StatusPill>}
        </div>

        {!listing ? (
          <p className="text-sm text-muted-foreground">Not on the store yet</p>
        ) : (
          <>
            {listing.title && <p className="text-sm font-medium">{listing.title}</p>}
            <dl className="grid grid-cols-[auto_minmax(0,1fr)] items-center gap-x-4 gap-y-1 text-sm">
              <dt className="text-xs text-muted-foreground">Price</dt>
              <dd className="flex min-w-0 flex-wrap items-center gap-x-2">
                {onSavePrice ? (
                  <POInlineEditableField
                    kind="money"
                    value={listing.price ?? ''}
                    displayValue={formatMoney(effectivePrice)}
                    placeholder="Set price"
                    ariaLabel="Listing price"
                    pending={pricePending}
                    showEditIcon
                    buttonClassName="-ml-2 font-semibold tabular-nums"
                    onConfirm={(raw) => onSavePrice(raw.trim() === '' ? null : raw.trim())}
                  />
                ) : (
                  <span className="font-semibold tabular-nums">{formatMoney(effectivePrice)}</span>
                )}
                {listing.compare_at_price && (
                  <s className="text-xs text-muted-foreground tabular-nums">{formatMoney(listing.compare_at_price)}</s>
                )}
                {listing.suggested_price != null && Number(listing.price) !== Number(listing.suggested_price) && (
                  <span className="text-xs text-muted-foreground tabular-nums">
                    Suggested {formatMoney(listing.suggested_price)}
                  </span>
                )}
              </dd>
              <dt className="text-xs text-muted-foreground">Available</dt>
              <dd className="font-medium tabular-nums">{listing.available_quantity}</dd>
            </dl>
            {(listing.storefront_url || listing.shopify_admin_url) && (
              <div className="flex flex-wrap gap-x-4 gap-y-1">
                {listing.storefront_url && <ExternalLinkText href={listing.storefront_url}>Store page</ExternalLinkText>}
                {listing.shopify_admin_url && (
                  <ExternalLinkText href={listing.shopify_admin_url}>Shopify admin</ExternalLinkText>
                )}
              </div>
            )}
            {listing.last_push_error && (
              <div className="flex items-start justify-between gap-2 border-t pt-3">
                <p className="text-sm text-destructive">{listing.last_push_error}</p>
                {onPushNow && (
                  <LoadingButton
                    size="sm"
                    variant="outline"
                    className="h-7 shrink-0 gap-1 px-2 text-xs"
                    loading={pushPending}
                    onClick={onPushNow}
                  >
                    <RefreshCw className="size-3.5" aria-hidden /> Try again
                  </LoadingButton>
                )}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  )
}
